<template>
  <div v-if="hasAvailability" class="sp-finder-result-hours-badge" :class="classModifiers" part="base">
    <sp-button
      v-if="hasExpandableDetails"
      class="sp-finder-result-hours-badge__trigger"
      variant="text"
      densitysmcompact="false"
      :aria-expanded="isExpanded.toString()"
      :aria-label="triggerAriaLabel"
      part="trigger"
      @click="toggle"
    >
      <span class="sp-finder-result-hours-badge__content">
        <sp-chip class="sp-finder-result-hours-badge__status" variant="plain" :color="statusColor">
          <sp-icon slot="prepend" class="sp-finder-result-hours-badge__icon" name="time-filled"></sp-icon>
          {{ statusLabel }}
        </sp-chip>
        <span class="sp-finder-result-hours-badge__summary">
          {{ summaryLabel }}
          <sp-caret class="sp-finder-result-hours-badge__caret" :active="isExpanded" size="small"></sp-caret>
        </span>
      </span>
    </sp-button>

    <span v-else class="sp-finder-result-hours-badge__content" part="trigger">
      <sp-chip class="sp-finder-result-hours-badge__status" variant="plain" :color="statusColor">
        <sp-icon slot="prepend" class="sp-finder-result-hours-badge__icon" name="time-filled"></sp-icon>
        {{ statusLabel }}
      </sp-chip>
      <span class="sp-finder-result-hours-badge__summary">{{ summaryLabel }}</span>
    </span>

    <transition name="expand">
      <div v-if="showHoursDetails" class="sp-finder-result-hours-badge__panel" part="panel">
        <dl v-if="hoursRows.length" class="sp-finder-result-hours-badge__list">
          <div v-for="row in hoursRows" :key="`${row.day}-${row.times}`" class="sp-finder-result-hours-badge__row">
            <dt class="sp-finder-result-hours-badge__day">{{ row.day }}</dt>
            <dd class="sp-finder-result-hours-badge__times">{{ row.times }}</dd>
          </div>
        </dl>
        <p v-else-if="alwaysOpenText" class="sp-finder-result-hours-badge__note">{{ alwaysOpenText }}</p>
      </div>
    </transition>

    <div v-if="hasSupportingDetails" class="sp-finder-result-hours-badge__supporting-details" part="supporting-details">
      <!-- Notes are intentionally escaped ({{ }}), not v-html — specialNote is admin free-text. -->
      <!-- TODO: for link support, pass sanitized/structured content, not raw v-html. -->
      <p v-if="note" class="sp-finder-result-hours-badge__note">{{ note }}</p>
      <p v-if="specialNote" class="sp-finder-result-hours-badge__note">{{ specialNote }}</p>

      <div v-if="specialHours.length" class="sp-finder-result-hours-badge__special-hours">
        <h3 class="sp-finder-result-hours-badge__special-hours-title">{{ specialHoursHeading }}</h3>
        <dl class="sp-finder-result-hours-badge__special-hours-list">
          <div
            v-for="specialHour in specialHours"
            :key="`${specialHour.name}-${specialHour.text}`"
            class="sp-finder-result-hours-badge__row"
          >
            <dt class="sp-finder-result-hours-badge__day">{{ specialHour.name }}</dt>
            <dd class="sp-finder-result-hours-badge__times">{{ specialHour.text }}</dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useAddressAvailability } from "#ce/composables/useAddressAvailability";

const props = defineProps({
  availability: {
    type: Object,
    default: undefined,
  },
});

const isExpanded = ref(false);

const {
  hasAvailability,
  hasExpandableDetails,
  isCurrentlyOpen,
  isClosingSoon,
  statusLabel,
  summaryLabel,
  hoursRows,
  specialHours,
  specialHoursHeading,
  specialNote,
  note,
  alwaysOpenText,
  toggleLabel,
} = useAddressAvailability(() => props.availability);

const triggerAriaLabel = computed(() => toggleLabel(isExpanded.value));
const showHoursDetails = computed(() => isExpanded.value && hasExpandableDetails.value);
const hasSupportingDetails = computed(() => Boolean(note.value || specialNote.value || specialHours.value.length));

const statusColor = computed(() => {
  if (isClosingSoon.value) {
    return "warning";
  }

  return isCurrentlyOpen.value ? "success" : "danger";
});

const classModifiers = computed(() => ({ [`--status-${statusColor.value}`]: true }));

function toggle() {
  isExpanded.value = !isExpanded.value;
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-finder-result-hours-badge {
  /* Layout */
  --trigger-gap: var(--sp-comp-finder-result-hours-badge-trigger-gap, var(--sp-ref-scale-8));
  --trigger-summary-caret-gap: var(
    --sp-comp-finder-result-hours-badge-trigger-summary-caret-gap,
    var(--sp-ref-scale-4)
  );
  --trigger-min-height: var(--sp-comp-finder-result-hours-badge-trigger-min-height, var(--icon-size));
  --panel-width: var(--sp-comp-finder-result-hours-badge-panel-width, 22rem);
  --panel-gap: var(--sp-comp-finder-result-hours-badge-panel-gap, var(--sp-ref-scale-4));
  --panel-row-gap: var(--sp-comp-finder-result-hours-badge-panel-row-gap, var(--sp-ref-scale-4));
  --panel-margin-block-start: var(--sp-comp-finder-result-hours-badge-panel-margin-block-start, var(--sp-ref-scale-8));

  /* Color */
  --trigger-color: var(--sp-comp-finder-result-hours-badge-trigger-color, var(--sp-sys-color-on-surface));
  --status-open-color: var(--sp-comp-finder-result-hours-badge-status-open-color, var(--sp-sys-color-success, #00804d));
  --status-closing-color: var(
    --sp-comp-finder-result-hours-badge-status-closing-color,
    var(--sp-sys-color-warning, #ad5c00)
  );
  --status-closed-color: var(
    --sp-comp-finder-result-hours-badge-status-closed-color,
    var(--sp-sys-color-danger, #dd1d26)
  );
  --summary-color: var(--sp-comp-finder-result-hours-badge-summary-color, var(--sp-sys-color-on-surface));

  /* Typography */
  --trigger-font: var(--sp-comp-finder-result-hours-badge-trigger-font, var(--sp-sys-typescale-body-short));
  --status-font-weight: var(
    --sp-comp-finder-result-hours-badge-status-font-weight,
    var(--sp-sys-typescale-body-bold-font-weight)
  );
  --panel-font: var(--sp-comp-finder-result-hours-badge-panel-font, var(--sp-sys-typescale-body-small-short));
  --panel-day-font: var(--sp-comp-finder-result-hours-badge-panel-day-font, var(--sp-sys-typescale-body-bold-short));

  /* Icon */
  --icon-size: var(--sp-comp-finder-result-hours-badge-icon-size, var(--sp-sys-icon-size-small));

  --status-color: var(--status-closed-color);
  --content-inline-start: calc(var(--icon-size) + var(--trigger-gap));

  position: relative;
  max-inline-size: 100%;
  inline-size: 100%;

  &.--status-success {
    --status-color: var(--status-open-color);
  }

  &.--status-warning {
    --status-color: var(--status-closing-color);
  }

  &__trigger {
    --sp-comp-button-primary-variant-text-text-color: var(--trigger-color);
    --sp-comp-button-primary-hover-variant-text-text-color: var(--trigger-color);
    --sp-comp-button-primary-hover-variant-text-bg-opacity: 0;
    --sp-comp-button-padding: 0;
    --sp-comp-button-size: var(--trigger-min-height);
    --sp-comp-button-min-width: auto;
    --sp-comp-button-font-short: var(--trigger-font);
    --sp-comp-button-content-text-align: start;

    max-inline-size: 100%;
  }

  &__content {
    display: inline-grid;
    grid-template-columns: auto auto;
    align-items: center;
    gap: var(--trigger-gap);
    min-height: var(--trigger-min-height);
    max-inline-size: 100%;
    color: var(--trigger-color);
    font: var(--trigger-font);
    text-align: start;
  }

  &__status {
    --sp-comp-chip-fill-color: var(--status-color);
    --sp-comp-chip-padding-block: 0;
    --sp-comp-chip-border-width: 0;
    --sp-comp-chip-gap: var(--trigger-gap);
    --sp-comp-chip-font-size: inherit;
    --sp-comp-chip-font-weight: var(--status-font-weight);
    --sp-comp-chip-line-height: inherit;

    text-transform: uppercase;
  }

  &__icon {
    --sp-comp-icon-size: var(--icon-size);
    --sp-comp-icon-fill: var(--status-color);
  }

  &__summary {
    display: inline-flex;
    align-items: center;
    gap: var(--trigger-summary-caret-gap);
    color: var(--summary-color);
  }

  &__caret {
    display: flex;
  }

  &__panel {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--panel-gap);
    inline-size: min(var(--panel-width), calc(100% - var(--content-inline-start)));
    margin-block-start: var(--panel-margin-block-start);
    margin-inline-start: var(--content-inline-start);
    color: var(--trigger-color);
    font: var(--panel-font);
  }

  &__supporting-details {
    display: flex;
    flex-direction: column;
    gap: var(--panel-gap);
    margin-block-start: var(--panel-margin-block-start);
    margin-inline-start: var(--content-inline-start);
    color: var(--trigger-color);
    font: var(--panel-font);
  }

  &__list,
  &__special-hours,
  &__special-hours-list {
    display: flex;
    flex-direction: column;
    gap: var(--panel-row-gap);
    margin: 0;
  }

  &__special-hours-title {
    margin: 0;
    font: var(--panel-day-font);
  }

  &__row {
    display: grid;
    align-items: baseline;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: var(--sp-ref-scale-12);
  }

  &__day {
    font: var(--panel-day-font);
  }

  &__times {
    margin: 0;
  }

  &__note {
    margin: 0;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all var(--sp-ref-transition-expand-duration) var(--sp-ref-transition-expand-easing);
  overflow: hidden;
  max-height: var(--sp-comp-finder-result-hours-badge-expand-max-height, 40rem);
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
